import React, { useEffect, useRef, useState } from "react";
import { Event, Filter } from "nostr-tools";
import { Box, Button, ButtonBase, CircularProgress, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/AddRounded";
import { useNavigate } from "react-router-dom";
import { dataLayer } from "@formstr/local-relay";
import { useRelayHydrated, useRelayRefresh } from "../../dataLayer/hooks";
import { useUserContext } from "../../hooks/useUserContext";
import { useListContext } from "../../hooks/useListContext";
import { useSubNav } from "../../contexts/SubNavContext";
import { FollowPackCard } from "../FollowPacks/FollowPackCard";
import { WhoToFollow } from "../Profile/WhoToFollow";
import { safeSetItem } from "../../utils/localStorage";

const STORAGE_KEY = "pollerama:followPacksSource";
const BATCH_SIZE = 20;

type Source = "global" | "following" | "mine" | "bookmarked";

const FollowPacksFeed: React.FC = () => {
  const { user } = useUserContext();
  const { lists, bookmarkedPackKeys } = useListContext();
  const { setItems, clearItems } = useSubNav();
  const relayRefresh = useRelayRefresh();
  const navigate = useNavigate();
  // Wait for the worker's cache before the first request, so it replays cached
  // packs instead of EOSE-ing on an empty store and re-fetching everything.
  const hydrated = useRelayHydrated();

  const savedSource = (localStorage.getItem(STORAGE_KEY) as Source) || "global";
  const [source, setSource] = useState<Source>(savedSource);
  const [packs, setPacks] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [cursor, setCursor] = useState<number | undefined>(undefined);
  const seen = useRef<Set<string>>(new Set());

  // Bookmarked packs come straight from context — no relay fetch needed
  const bookmarkedPacks = Array.from(lists?.entries() || [])
    .filter(([key, e]) => e.kind === 39089 && bookmarkedPackKeys.has(key))
    .map(([, e]) => e)
    .sort((a, b) => b.created_at - a.created_at);

  // The user's own packs, and packs others have put them in. lists-context
  // already fetches both on login (authors: me / #p: me).
  const allPacks = Array.from(lists?.values() || []).filter((e) => e.kind === 39089);
  const myPacks = allPacks
    .filter((e) => e.pubkey === user?.pubkey)
    .sort((a, b) => b.created_at - a.created_at);
  const packsImIn = allPacks
    .filter(
      (e) =>
        e.pubkey !== user?.pubkey &&
        e.tags.some((t) => t[0] === "p" && t[1] === user?.pubkey),
    )
    .sort((a, b) => b.created_at - a.created_at);

  // Register sub-nav items
  useEffect(() => {
    const select = (s: Source) => {
      setSource(s);
      safeSetItem(STORAGE_KEY, s);
      setPacks([]);
      seen.current.clear();
      setCursor(undefined);
      setInitialLoadComplete(false);
    };

    setItems([
      {
        key: "global",
        label: "Global",
        active: source === "global",
        onClick: () => select("global"),
      },
      {
        key: "following",
        label: "Following",
        active: source === "following",
        disabled: !user,
        onClick: () => select("following"),
      },
      {
        key: "mine",
        label: "Mine",
        active: source === "mine",
        disabled: !user,
        onClick: () => select("mine"),
      },
      {
        key: "bookmarked",
        label: "Bookmarked",
        active: source === "bookmarked",
        disabled: !user,
        onClick: () => select("bookmarked"),
      },
    ]);

    return () => clearItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, user]);

  const fetchBatch = () => {
    if (loading || source === "bookmarked" || source === "mine") return;
    setLoading(true);

    const now = Math.floor(Date.now() / 1000);
    const newPacks: Event[] = [];
    let oldestTimestamp: number | undefined;

    const filter: Filter = {
      kinds: [39089],
      limit: BATCH_SIZE,
      until: cursor ?? now,
    };

    if (source === "following" && user?.follows?.length) {
      filter.authors = user.follows;
    }

    let settled = false;
    let quietTimer: ReturnType<typeof setTimeout> | null = null;

    const handle = dataLayer.observe([filter], {
      onEvent: (event: Event) => {
        if (!seen.current.has(event.id)) {
          seen.current.add(event.id);
          newPacks.push(event);
        }
        if (!oldestTimestamp || event.created_at < oldestTimestamp) {
          oldestTimestamp = event.created_at;
        }
        // Commit shortly after the stream goes quiet (local EOSE precedes the
        // worker's upstream fetch under the dataLayer contract).
        if (quietTimer) clearTimeout(quietTimer);
        quietTimer = setTimeout(finalize, 900);
      },
    });

    const finalize = () => {
      if (settled) return;
      settled = true;
      if (quietTimer) clearTimeout(quietTimer);
      handle.unobserve();
      setPacks((prev) => {
        const ids = new Set(prev.map((e) => e.id));
        const merged = [...prev, ...newPacks.filter((e) => !ids.has(e.id))];
        merged.sort((a, b) => b.created_at - a.created_at);
        return merged;
      });
      if (oldestTimestamp) setCursor(oldestTimestamp - 1);
      setInitialLoadComplete(true);
      setLoading(false);
    };

    // Hard cap so an empty result (no events → no quiet timer) still settles.
    setTimeout(finalize, 5000);
  };

  useEffect(() => {
    if (!hydrated) return; // wait for the cache before the first request
    if (source !== "bookmarked" && source !== "mine") fetchBatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, hydrated, relayRefresh]);

  const local = source === "bookmarked" || source === "mine";
  const displayPacks = source === "bookmarked" ? bookmarkedPacks : packs;
  const isLoading = local ? false : loading;
  const isDone = local ? true : initialLoadComplete;

  const emptyMessage =
    source === "bookmarked"
      ? "No bookmarked packs yet. Bookmark packs from the Global or Following feeds."
      : source === "following"
      ? "No follow packs found from people you follow."
      : "No follow packs found.";

  const grid = (children: React.ReactNode) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        alignItems: "start",
        maxWidth: 900,
        mx: "auto",
      }}
    >
      {children}
    </Box>
  );

  if (source === "mine") {
    return (
      <Box sx={{ height: "100%", overflowY: "auto", pb: 2 }}>
        {grid(
          <>
            <ButtonBase
              onClick={() => navigate("/feeds/follow-packs/new")}
              sx={{
                m: 1,
                minHeight: 140,
                borderRadius: "24px",
                border: "2px dashed",
                borderColor: "divider",
                flexDirection: "column",
                gap: 1,
                fontFamily: "inherit",
                fontWeight: 800,
                fontSize: 15,
                color: "text.secondary",
                "&:hover": { borderColor: "text.secondary", color: "text.primary" },
              }}
            >
              <AddIcon sx={{ fontSize: 32 }} />
              New pack
            </ButtonBase>
            {myPacks.map((pack) => (
              <FollowPackCard key={pack.id} event={pack} />
            ))}
          </>,
        )}
        {myPacks.length === 0 && (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ px: 3, py: 2 }}>
            Packs you make show up here. A pack is a list of people others can follow in one tap.
          </Typography>
        )}
        {packsImIn.length > 0 && (
          <>
            <Typography
              sx={{
                maxWidth: 900,
                mx: "auto",
                px: 2,
                pt: 3,
                pb: 0.5,
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: ".06em",
                textTransform: "uppercase",
                color: "text.secondary",
              }}
            >
              Packs you're in
            </Typography>
            {grid(packsImIn.map((pack) => <FollowPackCard key={pack.id} event={pack} />))}
          </>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", overflowY: "auto" }}>
      {/* Algorithmic follow suggestions sit atop the discovery (Global) tab —
          packs are curated lists to follow, so "people you may know" fits here.
          Renders nothing until the WoT worker has produced recommendations. */}
      {source === "global" && <WhoToFollow />}
      {isLoading && displayPacks.length === 0 ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : displayPacks.length === 0 && isDone ? (
        <Box display="flex" justifyContent="center" px={3} py={8}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        grid(displayPacks.map((pack) => <FollowPackCard key={pack.id} event={pack} />))
      )}

      {source !== "bookmarked" && initialLoadComplete && packs.length > 0 && (
        <Box display="flex" justifyContent="center" my={2}>
          <Button variant="contained" disabled={loading} onClick={fetchBatch}>
            {loading ? <CircularProgress size={24} /> : "Load More"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FollowPacksFeed;
